import * as Leaflet from 'leaflet';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { MapLayerTypes, MapMarker } from '../interfaces';
import { Country } from '@shared/constants/country';
import { UserMetadataService } from '@core/services/user-metadata.service';
import {
  MarkerType,
  endIconPreset,
  markerIconPreset,
  startIconPreset,
} from '../constants/constants';

@Injectable()
export class MapOptionsService {
  private translate = inject(TranslateService);
  private userMetaDataservice = inject(UserMetadataService);

  addBaseControls(map: Leaflet.Map, isFullScreen: boolean): void {
    const baseMapLayers = this.getMapBaseLayers();
    const layers = Leaflet.control.layers(baseMapLayers);
    layers.addTo(map);

    baseMapLayers.Vector.addTo(map);

    Leaflet.control
      .zoom({
        position: 'bottomright',
      })
      .addTo(map);

    if (isFullScreen) {
      Leaflet.DomUtil.addClass(
        layers.getContainer() as HTMLElement,
        'full-screen'
      );
    }
  }

  addMapRefreshControl(map: Leaflet.Map, mapRefreshFn: () => void): void {
    const mapRefreshButton = Leaflet.Control.extend({
      options: {
        position: 'topright',
        title: this.translate.instant('General.RefreshMapData'),
        onClick: mapRefreshFn,
        iconHtml: '<span class="dv-icons">refresh</span>',
      },

      onAdd() {
        const container = Leaflet.DomUtil.create(
          'div',
          'leaflet-bar leaflet-control refresh-control'
        );

        const button = Leaflet.DomUtil.create('a', '', container);
        button.href = '#';
        button.title = this.options.title;
        button.setAttribute('role', 'button');
        button.innerHTML = this.options.iconHtml;

        Leaflet.DomEvent.disableClickPropagation(button);
        Leaflet.DomEvent.disableScrollPropagation(button);
        Leaflet.DomEvent.on(button, 'click', e => {
          Leaflet.DomEvent.stop(e);
          this.options.onClick();
        });
        return container;
      },
    });

    const mapDataRefreshControl = new mapRefreshButton();
    mapDataRefreshControl.addTo(map);
  }

  getTooltipClass(markerType?: MarkerType): Leaflet.TooltipOptions {
    if (
      markerType &&
      (markerType === MarkerType.Start || markerType === MarkerType.End)
    ) {
      return {
        className: 'distance-tooltip',
        opacity: 0.7,
      };
    } else {
      return {
        className: 'info-tooltip',
        opacity: 1,
      };
    }
  }

  getMarkerIcon(
    markerType?: MarkerType,
    marker?: MapMarker
  ):
    | {
        icon: Leaflet.Icon<Leaflet.IconOptions> | Leaflet.DivIcon;
      }
    | undefined {
    switch (markerType) {
      case MarkerType.Start:
        return { icon: Leaflet.icon(startIconPreset) };
      case MarkerType.End:
        return { icon: Leaflet.icon(endIconPreset) };
      case MarkerType.Poi: {
        const iconSize = (marker?.options?.customMarker?.iconSize || [
          24, 24,
        ]) as [number, number];
        const iconAnchor = [
          iconSize[0] / 2,
          iconSize[1] / 2,
        ] as Leaflet.PointExpression;
        return {
          icon: Leaflet.divIcon({
            html: marker?.options?.customMarker?.iconHtml,
            className: 'poi-custom-marker',
            iconSize: iconSize,
            iconAnchor,
          }),
        };
      }
      case MarkerType.CustomDefault:
        return marker && marker.options?.customMarker
          ? {
              icon: Leaflet.icon({
                ...marker.options.customMarker,
                iconUrl:
                  marker.options.customMarker.iconUrl ||
                  markerIconPreset.iconUrl,
                iconAnchor:
                  marker.options.customMarker.iconAnchor ||
                  this.getCenterIconAnchor(
                    marker.options.customMarker.iconSize
                  ),
                className: marker.options.customMarker.disableHoverAnimation
                  ? `custom-marker-no-hover .${marker.options.customMarker.className}`
                  : `custom-marker .${marker.options.customMarker.className}`,
              }),
            }
          : { icon: Leaflet.icon(markerIconPreset) };
      default:
        return {
          icon: Leaflet.icon(
            marker?.options?.customMarker
              ? {
                  ...marker.options.customMarker,
                  iconAnchor:
                    marker.options.customMarker.iconAnchor ||
                    this.getCenterIconAnchor(
                      marker.options.customMarker.iconSize
                    ),
                }
              : markerIconPreset
          ),
        };
    }
  }

  getMarkerClusterOptions(): Leaflet.MarkerClusterGroupOptions {
    return {
      spiderfyOnMaxZoom: false,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: false,
    };
  }

  private getCenterIconAnchor(
    iconSize?: Leaflet.PointExpression
  ): Leaflet.PointExpression | undefined {
    if (!iconSize) return undefined;
    const { x, y } = Leaflet.point(iconSize);
    return [x / 2, y / 2];
  }

  private getMapBaseLayers(): Record<MapLayerTypes, L.TileLayer> {
    const userCountry = this.userMetaDataservice.getUserCountry();

    if (userCountry !== Country.Norway) {
      const userLang = this.translate.currentLang;
      return {
        Vector: Leaflet.tileLayer(
          `http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=${userLang}&key=AIzaSyC8N0OWlc5agNSYtpFpfQ22e68r-G6-3ug`,
          {
            maxZoom: 20,
            subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
          }
        ),
        Aerial: Leaflet.tileLayer(
          `http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}&hl=${userLang}&key=AIzaSyC8N0OWlc5agNSYtpFpfQ22e68r-G6-3ug`,
          {
            maxZoom: 20,
            subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
          }
        ),
      };
    } else {
      return {
        Vector: Leaflet.tileLayer(
          `https://waapi.webatlas.no/maptiles/tiles/webatlas-standard-vektor/wa_grid/{z}/{x}/{y}.png?APITOKEN=${environment.leafletApiKey}`,
          { maxZoom: 20 }
        ),
        Aerial: Leaflet.tileLayer(
          `https://waapi.webatlas.no/maptiles/tiles/webatlas-orto-newup/wa_grid/{z}/{x}/{y}.jpeg?APITOKEN=${environment.leafletApiKey}`,
          { maxZoom: 20 }
        ),
      };
    }
  }
}
